import axios from "axios";
import { API_URL } from "../config/apiConfig";

const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
};

export const initPushNotifications = async () => {
    return initPushNotificationsWithOptions();
};

export const getPushConsentStatus = async () => {
    try {
        const token = localStorage.getItem("token");
        if (!token) {
            return "unknown";
        }

        const response = await axios.get(`${API_URL}/push-consent-status`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return response?.data?.consent || "unknown";
    } catch (error) {
        console.error("Failed to fetch push consent status:", error);
        return "unknown";
    }
};

export const savePushConsentStatus = async (consent) => {
    try {
        const token = localStorage.getItem("token");
        if (!token) {
            return false;
        }

        await axios.post(
            `${API_URL}/push-consent`,
            { consent },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );

        return true;
    } catch (error) {
        console.error("Failed to save push consent status:", error);
        return false;
    }
};

export const clearApplicationBadge = async () => {
    try {
        if (!("serviceWorker" in navigator)) {
            return;
        }

        if ("clearAppBadge" in navigator) {
            await navigator.clearAppBadge();
        }

        const registration = await navigator.serviceWorker.ready;
        if (registration?.active) {
            registration.active.postMessage({ type: "CLEAR_BADGE" });
        }
    } catch (error) {
        console.error("Clearing app badge failed:", error);
    }
};

export const initPushNotificationsWithOptions = async ({
    requestPermissionOnDefault = true,
} = {}) => {
    try {
        const token = localStorage.getItem("token");
        if (!token) {
            return { ok: false, reason: "no_token" };
        }

        if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
            return { ok: false, reason: "unsupported" };
        }

        if (Notification.permission === "denied") {
            return { ok: false, reason: "denied" };
        }

        if (Notification.permission === "default") {
            if (!requestPermissionOnDefault) {
                return { ok: false, reason: "permission_default" };
            }
            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                return { ok: false, reason: "permission_not_granted" };
            }
        }

        const registration = await navigator.serviceWorker.ready;

        let publicKey = null;
        try {
            const keyResponse = await axios.get(`${API_URL}/push-public-key`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            publicKey = keyResponse?.data?.publicKey;
        } catch (error) {
            if (error?.response?.status === 503) {
                return { ok: false, reason: "push_not_configured" };
            }
            throw error;
        }

        if (!publicKey) {
            return { ok: false, reason: "missing_public_key" };
        }

        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
            });
        }

        await axios.post(
            `${API_URL}/push-subscribe`,
            { subscription: subscription.toJSON() },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return { ok: true };
    } catch (error) {
        console.error("Push notification setup failed:", error);
        return { ok: false, reason: "setup_failed", error };
    }
};
